/**
 * WalletConnect 会话单例：连接、签名、恢复、断开。
 * 禁止在 UI 层直接 EthereumProvider.init。
 */

import { loadEthers } from '@/utils/load-ethers';
import { getExpectedChainId } from '@/utils/wallet-chain';
import { getPolygonWalletRpcUrl } from '@/utils/polygon-rpc';
import { createWalletLoginSignPayload } from '@/utils/wallet-login-message';
import { registerAuthTeardown } from '@/utils/auth-teardown';

const WALLETCONNECT_PROJECT_ID = process.env.VUE_APP_WALLETCONNECT_PROJECT_ID;

/** 支持 WalletConnect 扫码登录的 EVM 钱包 id */
export const WC_SCAN_WALLET_IDS = new Set([
  'walletconnect',
  'metamask',
  'coinbase',
  'okx',
  'bitget',
]);

let wcProvider = null;
let initPromise = null;
let connectTask = null;
let connectTaskGeneration = 0;
let initGeneration = 0;
/** 递增后使进行中的 pairing / 预取 URI 失效 */
let pairingGeneration = 0;
let pairingCleanupPromise = null;

let _wcErrorSuppressorInstalled = false;

function isWalletConnectKeychainNoise(msg, source = '', stack = '') {
  const lower = String(msg ?? '').toLowerCase();
  const isKeychain =
    lower.includes('no matching key') || lower.includes('pending session not found');
  if (!isKeychain) return false;
  const text = `${source || ''} ${stack || ''} ${lower}`;
  return (
    text.includes('walletconnect') ||
    text.includes('ethereum-provider') ||
    text.includes('index.es.js') ||
    lower.includes('keychain')
  );
}

function bumpPairingGeneration() {
  pairingGeneration += 1;
  return pairingGeneration;
}

async function awaitPairingCleanupIdle() {
  if (!pairingCleanupPromise) return;
  await pairingCleanupPromise.catch(() => {});
}

function schedulePairingCleanup(provider) {
  if (!provider) return Promise.resolve();
  const runPromise = (async () => {
    await pairingCleanupPromise?.catch(() => {});
    try {
      await prepareFreshWalletConnectPairing(provider);
    } catch (err) {
      console.warn('[wallet-connect] pairing cleanup failed', err);
    } finally {
      if (pairingCleanupPromise === runPromise) {
        pairingCleanupPromise = null;
      }
    }
  })();
  pairingCleanupPromise = runPromise;
  return runPromise;
}

function isStalePairingTask(taskGeneration) {
  return taskGeneration !== pairingGeneration;
}

function installWalletConnectErrorSuppressor() {
  if (_wcErrorSuppressorInstalled || typeof window === 'undefined') return;
  _wcErrorSuppressorInstalled = true;

  const isWcJsonNoise = (msg, source, stack) => {
    if (!msg || !msg.includes('is not valid JSON')) return false;
    const text = `${source || ''} ${stack || ''}`;
    return text.includes('walletconnect') || text.includes('index.es.js');
  };

  const shouldSuppress = (msg, source, stack) =>
    isWcJsonNoise(msg, source, stack) || isWalletConnectKeychainNoise(msg, source, stack);

  window.addEventListener(
    'error',
    (e) => {
      const msg = e.message || e.error?.message || '';
      if (shouldSuppress(msg, e.filename, e.error?.stack)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message ?? String(e.reason ?? '');
    const stack = e.reason?.stack ?? '';
    if (shouldSuppress(msg, '', stack)) e.preventDefault();
  });
}

async function normalizeEvmAddress(address) {
  const value = String(address ?? '').trim();
  if (!/^0x[0-9a-fA-F]{40}$/.test(value)) return value;
  try {
    const { getAddress } = await loadEthers();
    return getAddress(value);
  } catch {
    return value;
  }
}

function buildLoginMessage(address) {
  const normalized = String(address ?? '').trim();
  const { message } = createWalletLoginSignPayload(normalized);
  return message;
}

export function isWalletConnectConfigured() {
  return Boolean(String(WALLETCONNECT_PROJECT_ID ?? '').trim());
}

export function getWalletConnectProvider() {
  return wcProvider;
}

export function hasWalletConnectSession() {
  return Boolean(wcProvider?.session);
}

export function isWalletConnectSessionActive() {
  if (!wcProvider) return false;
  const accounts = wcProvider.accounts ?? [];
  return Boolean(wcProvider.session && accounts.length > 0);
}

function hasProviderAccounts(provider) {
  const accounts = provider?.accounts ?? [];
  return accounts.length > 0;
}

function isProviderOperational(provider) {
  return Boolean(provider?.session && typeof provider.request === 'function');
}

async function requestProviderAccounts(provider) {
  if (!isProviderOperational(provider)) return [];
  try {
    const accounts = await provider.request({ method: 'eth_accounts' });
    return Array.isArray(accounts) ? accounts : [];
  } catch (err) {
    console.warn('[wallet-connect] eth_accounts failed', err);
    return [];
  }
}

/** 刷新后 session 在但 accounts 为空时，尝试从持久化会话恢复 */
async function restoreWalletConnectAccounts(provider, { interactive = false } = {}) {
  if (!provider?.session) return false;
  if (hasProviderAccounts(provider)) return true;

  const viaAccounts = await requestProviderAccounts(provider);
  if (viaAccounts.length > 0) return true;

  try {
    const ns = provider.session.namespaces?.eip155;
    const rawAccounts = ns?.accounts;
    if (Array.isArray(rawAccounts) && rawAccounts.length > 0) {
      const first = String(rawAccounts[0] ?? '');
      const parts = first.split(':');
      if (parts.length >= 3 && parts[0] === 'eip155') {
        const sessionChainId = Number(parts[1]);
        if (Number.isFinite(sessionChainId) && sessionChainId !== provider.chainId) {
          provider.chainId = sessionChainId;
        }
      }
    }
  } catch (err) {
    console.warn('[wallet-connect] session chain reconcile failed', err);
  }

  if (hasProviderAccounts(provider)) return true;

  const afterChainFix = await requestProviderAccounts(provider);
  if (afterChainFix.length > 0) return true;

  if (!interactive) return false;

  try {
    await provider.enable();
  } catch (err) {
    console.warn('[wallet-connect] enable restore failed', err);
    return false;
  }

  return hasProviderAccounts(provider) || (await requestProviderAccounts(provider)).length > 0;
}

const WC_CONNECT_TIMEOUT_MS = 90_000;
const WC_SIGN_TIMEOUT_MS = 60_000;
/** 预取 pairing URI 的有效期（WalletConnect proposal 会过期） */
const WC_URI_PREFETCH_TTL_MS = 4 * 60 * 1000;

let pendingPairingUri = '';
let pendingPairingUriAt = 0;
let pendingPairingUriGeneration = 0;

/** @type {Set<(uri: string) => void>} */
const uriListeners = new Set();
/** @type {Set<() => void>} */
const connectListeners = new Set();

function clearPendingPairingUri() {
  pendingPairingUri = '';
  pendingPairingUriAt = 0;
  pendingPairingUriGeneration = 0;
}

function isPrefetchedUriValid() {
  if (!pendingPairingUri) return false;
  if (pendingPairingUriGeneration !== pairingGeneration) {
    clearPendingPairingUri();
    return false;
  }
  if (Date.now() - pendingPairingUriAt >= WC_URI_PREFETCH_TTL_MS) {
    clearPendingPairingUri();
    return false;
  }
  return true;
}

function notifyWalletConnectUri(uri) {
  const value = String(uri ?? '').trim();
  if (!value) return;
  pendingPairingUri = value;
  pendingPairingUriAt = Date.now();
  pendingPairingUriGeneration = pairingGeneration;
  uriListeners.forEach((listener) => {
    try {
      listener(value);
    } catch (err) {
      console.warn('[wallet-connect] uri listener failed', err);
    }
  });
}

function notifyWalletConnectSessionConnect() {
  connectListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn('[wallet-connect] connect listener failed', err);
    }
  });
}

/**
 * 订阅 WC pairing 事件；若已有有效预取 URI 会立即回调 onUri。
 * @returns {() => void} unsubscribe
 */
function subscribeWalletConnectPairingEvents({ onUri, onConnect } = {}) {
  if (typeof onUri === 'function') {
    if (isPrefetchedUriValid()) {
      onUri(pendingPairingUri);
    }
    uriListeners.add(onUri);
  }
  if (typeof onConnect === 'function') {
    connectListeners.add(onConnect);
  }
  return () => {
    if (typeof onUri === 'function') uriListeners.delete(onUri);
    if (typeof onConnect === 'function') connectListeners.delete(onConnect);
  };
}

export function getPrefetchedWalletConnectUri() {
  return isPrefetchedUriValid() ? pendingPairingUri : '';
}

export function isWalletConnectPairingInProgress() {
  return Boolean(connectTask) && connectTaskGeneration === pairingGeneration;
}

/** 预取 URI 与底层 pairing 仍同一代际时可秒开，否则需 reset */
export function canSafelyReuseWalletConnectPrefetch() {
  return isPrefetchedUriValid() && isWalletConnectPairingInProgress() && !pairingCleanupPromise;
}

/** 仅预热 SDK（IndexedDB + EthereumProvider.init） */
export async function warmupWalletConnect() {
  if (!isWalletConnectConfigured()) return;
  try {
    await initWalletConnectProvider();
  } catch (err) {
    console.warn('[wallet-connect] warmup failed', err);
    throw err;
  }
}

/** 扫码前确认 WC SDK 已 init（不 connect / 不 pairing） */
export async function ensureWalletConnectSdkReady({ timeoutMs = 8_000 } = {}) {
  if (!isWalletConnectConfigured()) return;
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('walletconnect sdk prepare timeout')), timeoutMs);
  });
  try {
    await Promise.race([warmupWalletConnect(), timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * 未登录时静默发起 pairing，缓存 display_uri 供扫码弹窗秒开。
 */
export async function prefetchWalletConnectPairing() {
  if (!isWalletConnectConfigured()) return;
  if (isPrefetchedUriValid() && isWalletConnectPairingInProgress()) return;
  await awaitPairingCleanupIdle();
  if (isPrefetchedUriValid() && isWalletConnectPairingInProgress()) return;
  void startWalletConnectPairingTask({ freshLogin: true });
}

function withTimeout(promise, ms, message) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/** 登录扫码前：断开失效会话并清理 pending pairing，避免 proposal 不匹配 */
async function prepareFreshWalletConnectPairing(provider) {
  if (!provider) return;
  try {
    if (provider.session) {
      await provider.disconnect();
    }
  } catch (err) {
    console.warn('[wallet-connect] disconnect stale session failed', err);
  }
  try {
    await provider.signer?.cleanupPendingPairings?.({ deletePairings: true });
  } catch (err) {
    console.warn('[wallet-connect] cleanup pairings failed', err);
  }
}

export async function resetWalletConnectLoginState() {
  bumpPairingGeneration();
  connectTask = null;
  connectTaskGeneration = 0;
  clearPendingPairingUri();
  uriListeners.clear();
  connectListeners.clear();
  if (!wcProvider) return;
  await schedulePairingCleanup(wcProvider);
}

async function clearStaleWalletConnectSession() {
  if (!wcProvider?.session) return;
  const provider = wcProvider;
  wcProvider = null;
  initPromise = null;
  initGeneration += 1;
  connectTask = null;
  try {
    await provider.disconnect();
  } catch (err) {
    console.warn('[wallet-connect] clear stale session failed', err);
  }
}

export function supportsWalletConnectScan(walletId) {
  return WC_SCAN_WALLET_IDS.has(
    String(walletId ?? '')
      .trim()
      .toLowerCase()
  );
}

/** @deprecated 使用 supportsWalletConnectScan */
export function supportsWalletConnectHover(walletId) {
  return supportsWalletConnectScan(walletId);
}

function hasInvalidRpcMap(provider) {
  const rpcMap = provider?.rpc?.rpcMap;
  if (!rpcMap || typeof rpcMap !== 'object') return false;
  return Object.values(rpcMap).some((url) => {
    const value = String(url ?? '').trim();
    return !value || value.startsWith('/') || !/^https?:\/\//i.test(value);
  });
}

const WC_IDB_NAME = 'WALLET_CONNECT_V2_INDEXED_DB';
const WC_IDB_STORE = 'keyvaluestorage';

function isWalletConnectStorageKey(key) {
  const lower = String(key ?? '').toLowerCase();
  return (
    lower.startsWith('wc@') ||
    lower.includes('walletconnect') ||
    lower.includes('wallet_connect') ||
    lower.startsWith('wc_')
  );
}

function clearWalletConnectLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter(isWalletConnectStorageKey)
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (err) {
    console.warn('[wallet-connect] clear localStorage failed', err);
  }
}

/** 修复 deleteDatabase 后遗留的空库（无 object store） */
async function ensureWalletConnectIdbStore() {
  if (typeof indexedDB === 'undefined') return;

  await new Promise((resolve, reject) => {
    const request = indexedDB.open(WC_IDB_NAME);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(WC_IDB_STORE)) {
        db.close();
        resolve();
        return;
      }
      db.close();
      const del = indexedDB.deleteDatabase(WC_IDB_NAME);
      del.onerror = () => reject(del.error);
      del.onsuccess = () => {
        const recreate = indexedDB.open(WC_IDB_NAME, 1);
        recreate.onupgradeneeded = () => {
          const next = recreate.result;
          if (!next.objectStoreNames.contains(WC_IDB_STORE)) {
            next.createObjectStore(WC_IDB_STORE);
          }
        };
        recreate.onerror = () => reject(recreate.error);
        recreate.onsuccess = () => {
          recreate.result.close();
          resolve();
        };
      };
      del.onblocked = () => resolve();
    };
  });
}

async function getWalletConnectIdbStore() {
  const { createStore } = await import('idb-keyval');
  return createStore(WC_IDB_NAME, WC_IDB_STORE);
}

/** 清理 localStorage + IndexedDB 中的 WalletConnect 缓存（不删库，避免 object store 丢失） */
async function clearAllWalletConnectStorage() {
  clearWalletConnectLocalStorage();
  if (typeof indexedDB === 'undefined') return;

  try {
    await ensureWalletConnectIdbStore();
    const { clear } = await import('idb-keyval');
    await clear(await getWalletConnectIdbStore());
  } catch (err) {
    console.warn('[wallet-connect] clear indexedDB keys failed', err);
  }
}

/** 检测 WC 缓存是否含非法 RPC（如 dev 代理路径 /polygon-rpc） */
async function hasCorruptWalletConnectStorage() {
  if (typeof window === 'undefined') return false;

  const isCorruptValue = (value) => String(value ?? '').includes('/polygon-rpc');

  try {
    const corruptLocal = Object.keys(window.localStorage).some((key) => {
      if (!isWalletConnectStorageKey(key)) return false;
      return isCorruptValue(window.localStorage.getItem(key));
    });
    if (corruptLocal) return true;
  } catch (err) {
    console.warn('[wallet-connect] scan localStorage failed', err);
  }

  if (typeof indexedDB === 'undefined') return false;

  try {
    await ensureWalletConnectIdbStore();
    const { keys, get } = await import('idb-keyval');
    const store = await getWalletConnectIdbStore();
    const allKeys = await keys(store);
    for (const key of allKeys) {
      const value = await get(key, store);
      const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
      if (isCorruptValue(text)) return true;
    }
  } catch (err) {
    console.warn('[wallet-connect] scan indexedDB failed', err);
  }

  return false;
}

async function prepareWalletConnectInit() {
  await ensureWalletConnectIdbStore();
  if (await hasCorruptWalletConnectStorage()) {
    console.warn('[wallet-connect] corrupt rpc cache detected, clearing storage');
    await clearAllWalletConnectStorage();
  }
}

function buildWalletConnectRpcMap(chainId) {
  const rpcUrl = getPolygonWalletRpcUrl();
  if (!rpcUrl || rpcUrl.startsWith('/') || !/^https?:\/\//i.test(rpcUrl)) {
    throw new Error(`WalletConnect rpc url invalid: ${rpcUrl || '(empty)'}`);
  }
  return {
    [chainId]: rpcUrl,
    [String(chainId)]: rpcUrl,
  };
}

async function resetWalletConnectProviderIfInvalid() {
  if (!wcProvider) return;
  if (!hasInvalidRpcMap(wcProvider)) return;
  console.warn('[wallet-connect] invalid rpc map detected, resetting provider');
  await disconnectWalletConnect();
}

async function initWalletConnectProvider() {
  await prepareWalletConnectInit();
  await resetWalletConnectProviderIfInvalid();

  if (wcProvider) return wcProvider;
  if (initPromise) return initPromise;

  const projectId = WALLETCONNECT_PROJECT_ID;
  if (!projectId) {
    throw new Error('WalletConnect 未配置，请在 .env 中设置 VUE_APP_WALLETCONNECT_PROJECT_ID');
  }

  installWalletConnectErrorSuppressor();

  const generation = ++initGeneration;

  initPromise = (async () => {
    const { EthereumProvider, OPTIONAL_EVENTS, OPTIONAL_METHODS } = await import(
      '@walletconnect/ethereum-provider'
    );
    const chainId = getExpectedChainId();
    const provider = await EthereumProvider.init({
      projectId,
      optionalChains: [chainId],
      optionalMethods: OPTIONAL_METHODS,
      optionalEvents: OPTIONAL_EVENTS,
      showQrModal: false,
      disableProviderPing: true,
      rpcMap: buildWalletConnectRpcMap(chainId),
      metadata: {
        name: 'DeepSage',
        description: 'DeepSage',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        icons: [],
      },
    });
    if (generation !== initGeneration) {
      try {
        await provider.disconnect();
      } catch {
        /* ignore stale init */
      }
      throw new Error('WalletConnect init cancelled');
    }
    wcProvider = provider;
    if (provider.session) {
      await restoreWalletConnectAccounts(provider, { interactive: false });
    }
    return provider;
  })();

  try {
    return await initPromise;
  } catch (err) {
    initPromise = null;
    wcProvider = null;
    throw err;
  }
}

/**
 * 尝试恢复已有 WC 会话（页面刷新后）
 * @returns {Promise<boolean>}
 */
export async function ensureWalletConnectReady() {
  if (!isWalletConnectConfigured()) return false;
  try {
    const provider = await initWalletConnectProvider();
    if (!provider?.session) return false;
    return restoreWalletConnectAccounts(provider, { interactive: true });
  } catch (err) {
    console.warn('[wallet-connect] ensure ready failed', err);
    return false;
  }
}

/**
 * 交易签名用：恢复 WC 会话并确保 accounts 可用
 * @returns {Promise<import('@walletconnect/ethereum-provider').default|null>}
 */
export async function ensureWalletConnectSigningProvider() {
  if (!isWalletConnectConfigured()) return null;
  try {
    const provider = await initWalletConnectProvider();
    if (!provider?.session) return null;
    const ready = await restoreWalletConnectAccounts(provider, { interactive: true });
    return ready && isProviderOperational(provider) ? provider : null;
  } catch (err) {
    console.warn('[wallet-connect] signing provider failed', err);
    return null;
  }
}

/**
 * 启动 pairing 任务（不阻塞到用户扫码；URI 通过 notifyWalletConnectUri 推送）
 * @param {{ freshLogin?: boolean, skipPrepare?: boolean }} [options]
 * @returns {Promise<import('@walletconnect/ethereum-provider').default>}
 */
function startWalletConnectPairingTask({ freshLogin = false, skipPrepare = false } = {}) {
  if (connectTask && connectTaskGeneration === pairingGeneration) {
    return connectTask;
  }

  const taskGeneration = pairingGeneration;

  const taskPromise = (async () => {
    await awaitPairingCleanupIdle();
    if (isStalePairingTask(taskGeneration)) {
      throw new Error('WalletConnect pairing cancelled');
    }

    const provider = await initWalletConnectProvider();
    if (isStalePairingTask(taskGeneration)) {
      throw new Error('WalletConnect pairing cancelled');
    }

    if (!freshLogin && provider.session) {
      const restored = await restoreWalletConnectAccounts(provider, { interactive: true });
      if (restored && isProviderOperational(provider)) {
        notifyWalletConnectSessionConnect();
        return provider;
      }
    }

    if (!skipPrepare) {
      await prepareFreshWalletConnectPairing(provider);
      if (isStalePairingTask(taskGeneration)) {
        throw new Error('WalletConnect pairing cancelled');
      }
      clearPendingPairingUri();
    }

    const onDisplayUri = (uri) => {
      if (!isStalePairingTask(taskGeneration)) notifyWalletConnectUri(uri);
    };
    const onSessionConnect = () => {
      if (!isStalePairingTask(taskGeneration)) notifyWalletConnectSessionConnect();
    };

    provider.on('display_uri', onDisplayUri);
    provider.on('connect', onSessionConnect);

    try {
      await withTimeout(provider.connect(), WC_CONNECT_TIMEOUT_MS, 'WalletConnect connect timeout');
      if (isStalePairingTask(taskGeneration)) {
        throw new Error('WalletConnect pairing cancelled');
      }
      if (!isProviderOperational(provider)) {
        throw new Error('WalletConnect session not ready');
      }
      return provider;
    } catch (err) {
      if (
        isStalePairingTask(taskGeneration) ||
        isWalletConnectKeychainNoise(err?.message, '', err?.stack)
      ) {
        throw new Error('WalletConnect pairing cancelled');
      }
      clearPendingPairingUri();
      await prepareFreshWalletConnectPairing(provider);
      throw err;
    } finally {
      provider.removeListener?.('display_uri', onDisplayUri);
      provider.removeListener?.('connect', onSessionConnect);
      if (connectTask === taskPromise) {
        connectTask = null;
        connectTaskGeneration = 0;
      }
    }
  })();

  connectTask = taskPromise;
  connectTaskGeneration = taskGeneration;

  taskPromise.catch((err) => {
    if (isStalePairingTask(taskGeneration)) return;
    if (isWalletConnectKeychainNoise(err?.message, '', err?.stack)) return;
    if (String(err?.message ?? '').includes('pairing cancelled')) return;
    console.warn('[wallet-connect] pairing task failed', err);
  });

  return taskPromise;
}

/**
 * 发起 WC 连接；display_uri 回调用于渲染二维码。
 * @param {{ onUri?: (uri: string) => void, onConnect?: () => void, signal?: AbortSignal, freshLogin?: boolean }} [options]
 * @returns {Promise<import('@walletconnect/ethereum-provider').default|null>}
 */
export async function connectWalletConnect(options = {}) {
  const { onUri, onConnect, signal, freshLogin = false } = options;

  const unsubscribe = subscribeWalletConnectPairingEvents({ onUri, onConnect });

  let aborted = false;
  const onAbort = () => {
    aborted = true;
    unsubscribe();
    void cancelPendingWalletConnect();
  };

  if (signal) {
    if (signal.aborted) {
      unsubscribe();
      return null;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    if (!connectTask) {
      startWalletConnectPairingTask({ freshLogin, skipPrepare: false });
    }
    const provider = await connectTask;
    if (aborted || signal?.aborted) return null;
    return provider;
  } catch (err) {
    if (aborted || signal?.aborted) return null;
    throw err;
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
    unsubscribe();
  }
}

/**
 * 使用已连接的 WC provider 完成登录验签
 * @param {import('@walletconnect/ethereum-provider').default} [provider]
 * @returns {Promise<{ address: string, signature: string, message: string }>}
 */
export async function signWalletLoginCredentials(provider = wcProvider) {
  if (!provider) {
    throw new Error('WalletConnect provider not found');
  }
  if (!isProviderOperational(provider)) {
    throw new Error('WalletConnect session not ready');
  }

  let accounts = provider.accounts ?? [];
  if (!accounts.length) {
    accounts = await requestProviderAccounts(provider);
  }
  const address = await normalizeEvmAddress(accounts[0]);
  if (!address) {
    throw new Error('WalletConnect：未获取到钱包地址');
  }

  const message = buildLoginMessage(address);
  const { BrowserProvider } = await loadEthers();
  const ethersProvider = new BrowserProvider(provider);
  const signer = await ethersProvider.getSigner();
  const signature = await withTimeout(
    signer.signMessage(message),
    WC_SIGN_TIMEOUT_MS,
    'WalletConnect sign timeout'
  );

  return { address, signature, message };
}

/**
 * 连接 + 签名（移动端等一次性流程）
 * @param {{ onUri?: (uri: string) => void, signal?: AbortSignal }} [options]
 * @returns {Promise<{ address, signature, message }|null>}
 */
export async function connectAndSignWalletLogin(options = {}) {
  const provider = await connectWalletConnect(options);
  if (!provider || options.signal?.aborted) return null;
  return signWalletLoginCredentials(provider);
}

/**
 * 断开 WC 并释放单例（登出 / 取消 pending 连接）
 */
export async function disconnectWalletConnect() {
  bumpPairingGeneration();
  connectTask = null;
  connectTaskGeneration = 0;
  clearPendingPairingUri();
  uriListeners.clear();
  connectListeners.clear();
  initGeneration += 1;
  const provider = wcProvider;
  wcProvider = null;
  initPromise = null;

  if (!provider) return;

  try {
    await provider.disconnect();
  } catch (err) {
    console.warn('[wallet-connect] disconnect failed', err);
  }
}

/** 仅取消进行中的连接任务，已成功登录的会话保留 */
export async function cancelPendingWalletConnect() {
  bumpPairingGeneration();
  connectTask = null;
  connectTaskGeneration = 0;
  clearPendingPairingUri();
  uriListeners.clear();
  connectListeners.clear();
  if (!wcProvider) return;
  if (isWalletConnectSessionActive()) return;
  await schedulePairingCleanup(wcProvider);
}

registerAuthTeardown(() => {
  void disconnectWalletConnect();
});
